import React, { useEffect, useState } from "react";
import { GetMonazamDetails, CreateExpense } from "../../../../Actions/RequestActions";
import { getCurrentDate, WarningToast, SuccessToast, ErrorToast } from "../../../../Util";
import { Spinner } from "react-bootstrap";
import "./ExpenseRequest.css";

const ExpenseRequestContainer = () => {
  const [values, setValues] = useState({
    currency: "",
    monazamBankAccountId: "",
    amount: "",
    narration: "",
  });

  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const handleSubmit = () => {
    const errors = {};

    if (!values.currency) {
      errors.currency = true;
    }
    
    if (values.currency === "PKR" && !values.monazamBankAccountId) {
      errors.monazamBankAccountId = true;
    }
    
    const amountValue = parseFloat(values.amount.replace(/,/g, ''));
    if (!values.amount || isNaN(amountValue) || amountValue <= 0) {
      errors.amount = true;
    }
    
    if (!values.narration?.trim()) {
      errors.narration = true;
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length === 0) {
      // Validate minimum amount (0.01)
      if (amountValue < 0.01) {
        WarningToast("Amount must be at least 0.01");
        setValidationErrors({ ...errors, amount: true });
        return;
      }

      CreateExpense(
        values,
        setLoading,
        setError,
        (response) => {
          // Handle nested response structure
          const message = response?.message || response?.data?.message || "Expense created successfully!";
          SuccessToast(message);
          // Reset form after successful submission
          setValues({
            currency: "",
            monazamBankAccountId: "",
            amount: "",
            narration: "",
          });
          setValidationErrors({});
        }
      );
    } else {
      WarningToast("Please fill in all required fields correctly.");
    }
  };

  const handleCurrencyChange = (e) => {
    const selectedCurrency = e.target.value;
    setValues({
      ...values,
      currency: selectedCurrency,
      // Clear monazamBankAccountId when currency changes to USD
      monazamBankAccountId: selectedCurrency === "USD" ? "" : values.monazamBankAccountId,
    });
    setValidationErrors({ ...validationErrors, currency: false, monazamBankAccountId: false });
  };

  const handleBankAccountChange = (e) => {
    setValues({ ...values, monazamBankAccountId: e.target.value });
    setValidationErrors({ ...validationErrors, monazamBankAccountId: false });
  };

  const formatAmountForDisplay = (value) => {
    if (!value) return "";
    // Remove commas for processing
    const valueWithoutCommas = value.replace(/,/g, "");
    // Check if it has decimal point
    if (valueWithoutCommas.includes(".")) {
      const parts = valueWithoutCommas.split(".");
      const integerPart = parts[0];
      const decimalPart = parts[1] || "";
      // Format integer part with commas
      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      // Combine with decimal part (up to 2 decimal places)
      return formattedInteger + "." + decimalPart.slice(0, 2);
    } else {
      // Format whole number with commas
      return valueWithoutCommas.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
  };

  const handleAmountChange = (e) => {
    const inputValue = e.target.value;
    // Remove commas for processing
    const valueWithoutCommas = inputValue.replace(/,/g, "");
    // Remove all non-numeric characters except decimal point
    const cleanValue = valueWithoutCommas.replace(/[^0-9.]/g, "");
    // Prevent multiple decimal points
    const parts = cleanValue.split(".");
    const formattedValue = parts.length > 2 
      ? parts[0] + "." + parts.slice(1).join("") 
      : cleanValue;
    // Limit to 2 decimal places
    const finalValue = formattedValue.includes(".") 
      ? formattedValue.split(".")[0] + "." + formattedValue.split(".")[1].slice(0, 2)
      : formattedValue;
    
    setValues({ ...values, amount: finalValue });
    setValidationErrors({ ...validationErrors, amount: false });
  };

  const handleNarrationChange = (e) => {
    setValues({ ...values, narration: e.target.value });
    setValidationErrors({ ...validationErrors, narration: false });
  };

  // Fetch bank accounts on component mount
  useEffect(() => {
    GetMonazamDetails(
      (data) => {
        if (data?.bankAccounts && Array.isArray(data.bankAccounts)) {
          const pkrAccounts = data.bankAccounts.filter(acc => acc.accountType === "PKR");
          setBankAccounts(pkrAccounts);
        }
      },
      setBankLoading,
      setBankError
    );
  }, []);

  return (
    <div className="container-fluid main-content-table">
      <div className="row">
        <div className="col-12">
          <div className="bg-light p-2 rounded">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h4 className="mb-0">Expense</h4>
              </div>
              <div className="d-flex flex-column">
                <label className="form-label small">Date</label>
                <input 
                  type="date" 
                  className="form-control date-input" 
                  value={getCurrentDate()} 
                  readOnly
                  onClick={(e) => e.target.showPicker()}
                />
              </div>
            </div>

            <div className="card">
              <div className="card-body p-2">
                <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                    <div className="row mb-2">
                      <div className="col-md-6">
                        <label className="form-label small">Currency *</label>
                        <select
                          className={`form-select ${validationErrors.currency ? 'is-invalid' : ''}`}
                          value={values.currency}
                          onChange={handleCurrencyChange}
                          disabled={loading}
                        >
                          <option value="">Select</option>
                          <option value="PKR">PKR</option>
                          <option value="USD">USD</option>
                        </select>
                        {validationErrors.currency && (
                          <div className="invalid-feedback">Please select a currency</div>
                        )}
                      </div>

                      {/* Right side of first row:
                          - PKR  -> Monazzam Account
                          - USD  -> Amount field
                      */}
                      <div className="col-md-6">
                        {values.currency === "PKR" && (
                          <>
                            <label className="form-label small">Monazzam Account *</label>
                            <select
                              className={`form-select ${validationErrors.monazamBankAccountId ? 'is-invalid' : ''}`}
                              value={values.monazamBankAccountId}
                              onChange={handleBankAccountChange}
                              disabled={bankLoading || loading}
                            >
                              <option value="" disabled hidden>
                                {bankLoading ? "Loading accounts..." : "Select"}
                              </option>
                              {bankAccounts.map((account) => (
                                <option key={account.id} value={account.id}>
                                  {account.bankName || account.accountTitle || account.iban} - {account.iban || account.accountNumber}
                                </option>
                              ))}
                            </select>
                            {validationErrors.monazamBankAccountId && (
                              <div className="invalid-feedback">Please select a Monazzam Account</div>
                            )}
                          </>
                        )}

                        {(values.currency === "USD" || !values.currency) && (
                          <>
                            <label className="form-label small">Amount *</label>
                            <input
                              type="text"
                              className={`form-control ${validationErrors.amount ? 'is-invalid' : ''}`}
                              placeholder="20,555"
                              value={formatAmountForDisplay(values.amount)}
                              onChange={handleAmountChange}
                              disabled={loading}
                            />
                            {validationErrors.amount && (
                              <div className="invalid-feedback">Please enter a valid amount (greater than 0)</div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Amount row (shown only for PKR).
                        For USD or when currency is not selected, amount is shown in the first row on the right side. */}
                    {values.currency === "PKR" && (
                      <div className="row mb-2">
                        <div className="col-md-6">
                          <label className="form-label small">Amount *</label>
                          <input
                            type="text"
                            className={`form-control ${validationErrors.amount ? 'is-invalid' : ''}`}
                            placeholder="20,555"
                            value={formatAmountForDisplay(values.amount)}
                            onChange={handleAmountChange}
                            disabled={loading}
                          />
                          {validationErrors.amount && (
                            <div className="invalid-feedback">Please enter a valid amount (greater than 0)</div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="row mb-2">
                      <div className="col-12">
                        <label className="form-label small">Narration *</label>
                        <textarea
                          className={`form-control ${validationErrors.narration ? 'is-invalid' : ''}`}
                          rows="4"
                          placeholder="Comment Here"
                          value={values.narration}
                          onChange={handleNarrationChange}
                          disabled={loading}
                        />
                        {validationErrors.narration && (
                          <div className="invalid-feedback">Please enter a narration</div>
                        )}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 d-flex justify-content-center">
                        <button
                          type="submit"
                          className="btn btn-success px-5"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Submitting...
                            </>
                          ) : (
                            "Submit"
                          )}
                        </button>
                      </div>
                    </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseRequestContainer;

